import { useState, useEffect, useCallback, useRef } from 'react';
import { sharedGet } from '../lib/api';

const POLL_INTERVAL = 30000; // 30 seconds

/**
 * Hook to track unread chat message count.
 * Polls /api/ws/chat/unread-count on interval.
 * Returns { unreadCount, refresh }.
 */
export default function useUnreadCount(enabled = true) {
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await sharedGet('/ws/chat/unread-count');
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch {
      // silently ignore - user may not be logged in
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    refresh();
    intervalRef.current = setInterval(refresh, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleUnreadChanged = () => refresh();
    window.addEventListener('snappipit:chat_unread_changed', handleUnreadChanged);
    return () => window.removeEventListener('snappipit:chat_unread_changed', handleUnreadChanged);
  }, [enabled, refresh]);

  return { unreadCount, refresh };
}
